const STORAGE_KEY = "dialetics_return_intent";
const MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes

type ReturnIntent = {
  url: string;
  source: string;
  timestamp: number;
};

/**
 * Ein Return-Intent gilt nur fuer **Inhalte**: eine gesperrte Lektion, ein
 * Artikel, ein Interview, eine Event-Anmeldung. Also Seiten, die der Nutzer
 * ansteuern wollte und wegen fehlender Anmeldung nicht bekam.
 *
 * Er gilt NICHT fuer beliebige Seiten, auf denen jemand zufaellig auf
 * "Anmelden" klickt. Wer das auf der Startseite tut, will danach ins
 * Dashboard — nicht wieder auf die Startseite.
 *
 * Genau das ist passiert: `AuthModalButton` setzte den Intent auf
 * `window.location.pathname`, ungeprueft. Auf der Startseite wurde daraus "/",
 * und der Nutzer landete nach dem Login wieder dort. Das sah wie eine
 * fehlgeschlagene Weiterleitung aus, war aber ein Intent, den es nie haette
 * geben duerfen.
 *
 * Statt zu raten, welche Pfade Inhalte sind — Artikel und Interviews laufen
 * ueber den Catch-all `[...slug]` und sind am Pfad nicht erkennbar —
 * entscheidet die Herkunft: Nur diese Quellen setzen einen Intent.
 *
 * Wer eine neue Quelle ergaenzt, traegt sie hier ein. Fehlt sie, wird der
 * Intent still verworfen; das ist die sichere Richtung, denn dann greift
 * einfach das Standardziel.
 */
const INHALTS_QUELLEN = [
  "lesson_access",
  "article_access",
  "interview_access",
  "event_signup",
  // Mitgliederexklusives Event (HeroEvent) — derselbe Fall wie ein gesperrter
  // Artikel: Der Nutzer wollte an den Inhalt und soll nach dem Kauf zurueck.
  "event_access",
  // Explizit uebergebenes Ziel aus /login?redirect=… — die Links dorthin
  // stammen aus denselben Inhaltsseiten (Lesson, Article, Interview, Event).
  "login_param",
  // Kein Inhalt, aber notwendig: Google fuehrt ueber eine fremde Seite. Ohne
  // den Intent kaeme der Nutzer nicht zur Kaufseite zurueck und der Checkout
  // braeche ab. Wird nur im skipRedirect-Fall gesetzt (PricingSection,
  // MembershipPaymentModal), also genau dort, wo ein Kauf laeuft.
  "google_oauth",
];

export function setReturnIntent(url: string, source: string): void {
  if (!INHALTS_QUELLEN.includes(source)) return;

  // Zusaetzliche Absicherung: Auch aus einer erlaubten Quelle darf kein Ziel
  // kommen, das nach dem Login sinnlos oder schaedlich waere. `login_param`
  // ist ein URL-Parameter und damit frei setzbar.
  const pfad = url.split("?")[0].replace(/\/$/, "") || "/";
  if (pfad === "/" || pfad === "/login" || pfad === "/signup") return;

  try {
    const intent: ReturnIntent = { url, source, timestamp: Date.now() };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(intent));
  } catch {}
}

export function getReturnIntent(): ReturnIntent | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const intent: ReturnIntent = JSON.parse(raw);
    if (Date.now() - intent.timestamp > MAX_AGE_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    // Prevent open redirect — only allow relative paths
    if (!intent.url.startsWith("/") || intent.url.startsWith("//")) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    // Auch beim Lesen pruefen: Intents auf "/" wurden bis zum Fix gespeichert
    // und liegen noch in laufenden Sitzungen.
    const pfad = intent.url.split("?")[0].replace(/\/$/, "") || "/";
    if (pfad === "/" || pfad === "/login" || pfad === "/signup") {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return intent;
  } catch {
    return null;
  }
}

export function consumeReturnIntent(): ReturnIntent | null {
  const intent = getReturnIntent();
  if (intent) {
    sessionStorage.removeItem(STORAGE_KEY);
  }
  return intent;
}
